import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { getNameExtension, isNotValidateSize } from '@shared/helpers';
import { useUploadFiles } from '@widgets/Files/hooks/useUploadFiles';
import { validatedFormat } from '@widgets/Files/model/data/validatedFormat';
import { TFolderId } from '@widgets/Files/model/types/folderId.type';
import { IUploadFile } from '@widgets/Files/model/types/uploadFile.interface';

import { useAppSelector } from '../../../store';
import { filesActions } from '../../../store/slices/filesSlice/filesSlice';

export const useFileUpload = (currentFolderName: string) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const existingUploads = useAppSelector((state) => state.filesReducer.uploadFiles);
    const [isError, setIsError] = useState(false);
    const [isAwaitingUpload, setIsAwaitingUpload] = useState(false);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const mutationUpload = useUploadFiles({
        onErrorCb: () => setIsError(true),
        fileNames,
        currentFolderName,
    });

    const onFileChange = useCallback(
        (files: File[], currentFolderId: TFolderId) => {
            setIsError(false);
            const names = Array.from(files).map((file) => {
                const { name } = getNameExtension(file.name);
                return name;
            });
            setFileNames(names);
            // setAwaitingUpload(true);
            if (files.length > 10) {
                // @ts-ignore
                swal({
                    text: t('useFileUpload.return.swal.manyFiles.text'),
                    cancel: false,
                    confirm: true,
                    button: {
                        text: t('useFileUpload.return.swal.manyFiles.button'),
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                });
                return;
            }

            const formData = new FormData();
            const preparedFiles: IUploadFile[] = [];
            let overallSize = 0;
            let hasError = false;

            files.forEach((file) => {
                const { extension } = getNameExtension(file.name);

                if (!validatedFormat.includes(extension.toLowerCase())) {
                    hasError = true;
                    // @ts-ignore
                    swal({
                        text: t('useFileUpload.return.swal.invalidFormat.text'),
                        cancel: false,
                        confirm: true,
                        button: {
                            text: t('useFileUpload.return.swal.invalidFormat.button'),
                            value: true,
                            visible: true,
                            className: 'btn btn-secondary',
                        },
                    });
                    return;
                }

                if (isNotValidateSize(file.size)) {
                    hasError = true;
                    // @ts-ignore
                    swal({
                        text: t('useFileUpload.return.swal.checkValidate.text'),
                        cancel: false,
                        confirm: true,
                        button: {
                            text: t('useFileUpload.return.swal.checkValidate.button'),
                            value: true,
                            visible: true,
                            className: 'btn btn-secondary',
                        },
                    });
                    return;
                }

                preparedFiles.push({
                    name: file.name,
                    id: 'load',
                    mimeType: 'image/jpeg',
                    originalUrl: '',
                    previewSvgUrl: '',
                    previewUrl: '',
                });

                formData.append('files[]', file);
                formData.append(
                    'folder_id',
                    !currentFolderId ? 'default' : String(currentFolderId)
                );
                overallSize += file.size;
            });

            // if (hasError) return;
            if (hasError) {
                setIsAwaitingUpload(false);
                setIsError(true);
                return;
            }

            if (isNotValidateSize(overallSize)) {
                // @ts-ignore
                swal({
                    text: t('useFileUpload.return.swal.isOverallSize.text'),
                    cancel: false,
                    confirm: true,
                    button: {
                        text: t('useFileUpload.return.swal.isOverallSize.button'),
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                });
                return;
            }

            setIsAwaitingUpload(true);
            dispatch(filesActions.changeUploadFiles([...existingUploads, ...preparedFiles]));
            // dispatch(filesActions.changeUploadFiles(preparedFiles));
            mutationUpload.mutate(formData);
        },
        [dispatch, t, mutationUpload, existingUploads]
    );

    return {
        onFileChange,
        isErrorUploading: isError,
        isAwaitingUpload,
        setIsAwaitingUpload,
        fileNames,
        setFileNames,
    };
};
